import { SUPPORTED_ENTITLEMENTS } from './types'

// Entitlement delta — ATC-129 / `ATC-074`.
//
// The server's edit is ADDITIVE/SUBTRACTIVE, never a whole-list replacement:
// a replacement makes accidental REMOVAL a one-typo operation on the set of
// services a live credential may call. So the console renders a checkbox set,
// and this module turns "what is ticked now" back into the two lists the API
// takes.

export interface EntitlementOption {
  value: string
  /** False when the console does not recognise the value — see `optionsFor`. */
  known: boolean
}

/**
 * THE SINGLE MOST IMPORTANT RULE IN THIS MODULE.
 *
 * The option set is seeded from `before ∪ SUPPORTED`, never from `SUPPORTED`
 * alone.
 *
 * An entitlement the console does not recognise must still render, and render
 * CHECKED — because a value absent from the set is a value `delta()` will
 * compute as removed and the request will SILENTLY STRIP. If the server ever
 * grows a sixth entitlement and this console is a deploy behind, the first
 * operator to edit entitlements on such an environment would quietly revoke it.
 *
 * Same principle as `ATC-ADR-018`'s "an unrecognised status renders warning,
 * never neutral": the console's ignorance of a value is never a reason to act
 * as though the value is not there.
 *
 * Ordering is stable — supported values first in their canonical order, then
 * unknown values sorted — so the set does not reshuffle between renders.
 */
export function optionsFor(before: readonly string[]): EntitlementOption[] {
  const supported = new Set<string>(SUPPORTED_ENTITLEMENTS)
  const unknown = [...new Set(before)].filter((v) => !supported.has(v)).sort()
  return [
    ...SUPPORTED_ENTITLEMENTS.map((value) => ({ value: value as string, known: true })),
    ...unknown.map((value) => ({ value, known: false })),
  ]
}

export interface EntitlementDelta {
  add: string[]
  remove: string[]
  /** True when neither list has an entry — the caller must not send a request. */
  empty: boolean
}

/**
 * Set difference both ways, sorted for a stable request body.
 *
 * CASE-SENSITIVE, deliberately: the server compares with `-cnotin`, so
 * `Bootstrap` is not `bootstrap` to it either. Folding case here would let the
 * console send an "add" the server then refuses as `entitlement_unsupported`,
 * with the operator looking at a value that reads correctly on screen.
 */
export function delta(before: readonly string[], after: readonly string[]): EntitlementDelta {
  const beforeSet = new Set(before)
  const afterSet = new Set(after)
  const add = [...afterSet].filter((v) => !beforeSet.has(v)).sort()
  const remove = [...beforeSet].filter((v) => !afterSet.has(v)).sort()
  return { add, remove, empty: add.length === 0 && remove.length === 0 }
}

/**
 * The server refuses an edit that would leave a credential entitled to nothing
 * (`entitlement_set_empty`), because that is not a revocation — `status` is,
 * and it is named, audited and reversible. An authenticate-but-authorize-nothing
 * credential would be a third state with no name.
 *
 * Checked here as well so the console can disable the button rather than let an
 * operator submit into a refusal.
 */
export function wouldEmptyTheSet(after: readonly string[]): boolean {
  return after.length === 0
}

/**
 * `ppkg` is grantable on `flightdeck`-class rows ONLY, enforced at issuance and
 * at validation (`ATC-ADR-016`). The console mirrors it so the box is disabled
 * with a stated reason rather than submitting into
 * `ppkg_requires_flightdeck_class`.
 */
export function isEntitlementAllowed(value: string, principalClass: 'device' | 'flightdeck'): boolean {
  if (value === 'ppkg') {
    return principalClass === 'flightdeck'
  }
  return true
}
